import { useContext } from 'react';
import styled from 'styled-components';

import AccountContext from 'contexts/AccountContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import FlexCentered from 'components/Common/FlexCentered';
import Typography from 'kingpin/atoms/Typography';

import { DriverBonusProgram, useSelectedDriver } from '.';
import ViewHomeDriverAppButton from './ViewHomeDriverAppButton';

// Column wrapper so the home-entry header stays pinned while the sections scroll.
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const CombinedHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 24px;
  border-bottom: 1px solid var(--color-border-secondary);
`;

// Each program renders as a bordered section; the tab itself owns the vertical scroll.
const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
`;

const ProgramSection = styled.div`
  display: flex;
  flex-direction: column;

  &:not(:last-of-type) {
    border-bottom: 8px solid var(--color-bg-secondary);
  }
`;

const SectionHeader = styled.div`
  padding: 16px 24px 0px 24px;
`;

/**
 * The combined driver bonus tab: stacks every bonus program the account runs into a single
 * scrollable tab, each as its own section with an independent period picker, override flow and
 * subtotal (each `DriverBonusProgram` owns its own period + overview providers). Used by the
 * `driver-bonus-combined` entity tab type.
 */
const DriverBonusOverviewCombined = () => {
  const driver = useSelectedDriver();
  const { bonusPrograms, bonusPeriodsLookup } = useContext(
    DatasetDefinitionsContext,
  );
  const { driverAppEmbedCarrierSlug } = useContext(AccountContext);

  if (!driver) {
    return (
      <FlexCentered>
        <Typography.Header type={'H3'}>
          This screen is only compatible with the driver entity
        </Typography.Header>
      </FlexCentered>
    );
  }

  if (bonusPrograms.length === 0) {
    return (
      <FlexCentered>
        <Typography.Header type={'H3'}>
          No bonus programs configured
        </Typography.Header>
      </FlexCentered>
    );
  }

  // The embed's home entry needs one program's document as the bootstrap; pick
  // the first program that has a non-archived period so the injected document
  // is a live one, falling back to the first program.
  const bootstrapProgram =
    bonusPrograms.find((program) =>
      (bonusPeriodsLookup[program.id] ?? []).some(
        (period) => period.status !== 'archived',
      ),
    ) ?? bonusPrograms[0];

  return (
    <Wrapper>
      {driverAppEmbedCarrierSlug && (
        <CombinedHeader>
          <ViewHomeDriverAppButton
            driver={driver}
            bootstrapProgram={bootstrapProgram}
          />
        </CombinedHeader>
      )}
      <ScrollContainer>
        {bonusPrograms.map((program) => (
          <ProgramSection key={program.id}>
            <SectionHeader>
              <Typography.Header type={'H2'}>{program.name}</Typography.Header>
            </SectionHeader>
            <DriverBonusProgram
              program={program}
              driver={driver}
              layout={'section'}
            />
          </ProgramSection>
        ))}
      </ScrollContainer>
    </Wrapper>
  );
};

export default DriverBonusOverviewCombined;
